import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { addMonths, addYears, format, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import CheckoutLegalProceedNotice from '@/components/legal/checkout-legal-proceed-notice';
import {
  getEffectivePlanChangeRaw,
  parseCheckoutCartPlanChange,
  useSubscriptionCheckoutCartQuery,
  type SubscriptionCheckoutCartLineDto,
  type SubscriptionCheckoutCartPlanLineTotal,
} from '@/state/queries/transactions/use-subscription-checkout-cart';
import { useCheckoutSubscriptionCart } from '@/state/mutations/transactions/use-checkout-subscription-cart';
import {
  usePatchOrganizationSubscriptionCheckoutCart,
  type PatchOrganizationSubscriptionCheckoutCartBody,
} from '@/state/mutations/transactions/use-patch-organization-subscription-checkout-cart';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans';
import type {
  SubscriptionBillingPeriod,
  SubscriptionPlan,
  SubscriptionTier,
} from '@/types/dtos/transactions/subscription-plan.dto';
import { getCheckoutRecurringPriceDisplay } from '@/helpers/subscription-checkout-recurring-price-display';
import {
  collectQuotaKeysFromPlan,
  computeOrganizationRenewalAmount,
  formatInclusiveLastServiceDayFromExclusiveEndUtc,
  formatQuotaDowngradeProceedAlert,
  formatQuotaStepperCountLine,
  formatQuotaStepperDeltaVsBooked,
  formatSubscriptionBillingInstantUtcMedium,
  formatSubscriptionInstancePeriodEndDisplay,
  getOveragePricePerUnit,
  getQuotaDisplayTotal,
  includedQuantityForKey,
  maxQuantityForKey,
  mergeOverageFromMatchingSubscriptionSnapshot,
  normalizeSubscriptionPlanOverage,
} from '@/helpers/organization-subscription-pricing';
import { resolveTranslatedProductField, resolveProductClaimText } from '@/helpers/resolve-translated-product-field';
import BillingForm from '@/components/organizations/edit-active-organization/tabs/billing/billing.form';
import { editActiveOrganizationSchemaFactory } from '@/schemas/organizations/edit-active-organization';
import type { Address } from '@/types/dtos/organizations/address.dto';
import { ADDRESS_TYPE } from '@/enums/address.enum';
import { useNotifications } from '@/hooks/use-notifications';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { ORGANIZATION_PERMISSIONS } from '@/enums/permissions.enum';
import { useEditOrganizationMutation } from '@/state/mutations/organizations/use-edit-organization';
import type { PatchOrganizationDto } from '@/types/dtos/organizations/request/patch-organization.dto';
import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import type { OrganizationSubscriptionInstanceDto } from '@/types/dtos/organizations/organization-subscription-instance.dto';
import type { EditActiveOrganization } from '@/components/organizations/edit-active-organization/edit.modal';
import { useTaxClassificationMutation } from '@/state/mutations/vat-validation/use-tax-classification';
import { COUNTRY_TO_CODE_MAP } from '@/constants';
import type { TaxClassification } from '@/types/dtos/organizations/response/tax-classification.dto';
import { capitalize } from '@/helpers/string.helper';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import BillingAddressDisplay from '@/components/events/event-catalog/billing-address-display';
import OrganizationSubscriptionPricingDisplay, {
  type OrganizationBookedPlanSummary,
} from '@/components/organizations/plan/organization-subscription-pricing-display';
import ManagePlanDialog from '@/components/organizations/plan/manage-plan-dialog';
import { getPrimaryOrganizationSubscriptionInstance } from '@/helpers/organization-subscriptions';
import {
  type CatalogProductLine,
  defaultMainSubscriptionProductKeyForCategory,
  findOrganizationInstanceForProduct,
  groupOrganizationProductLines,
  plansForBillingWindow,
  resolveParentBillingPeriodForAddonFromOrgAndCart,
  soleTierForProductLine,
  SUBSCRIPTION_CATALOG_CATEGORY_ORDER,
  subscriptionCategoryKey,
  uniqueTierCountForProductLine,
} from '@/helpers/organization-plan-catalog';
import type { TranslationKey } from '@/i18n/translation-key';
import type { ServerError } from '@/types/errors/server.error';

const HOST_DEFAULT_PRODUCT_KEY = defaultMainSubscriptionProductKeyForCategory('HOST');

/**
 * Resolve the catalog `SubscriptionPlan` for this cart line. Must include `subscriptionProductKey`;
 * matching only tier + billing would return the first matching row in the org catalog (usually
 * `host_default`) and show the wrong quota steppers for add-on lines.
 */
function pickPlanForProductLine(
  plans: SubscriptionPlan[],
  subscriptionProductKey: string,
  tier: SubscriptionTier,
  billing: SubscriptionBillingPeriod,
): SubscriptionPlan | undefined {
  const want = subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const withKey =
    plans.find(
      (p) =>
        (p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === want &&
        p.tier === tier &&
        (p.billingPeriod ?? 'MONTH') === billing,
    ) ?? plans.find((p) => (p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === want && p.tier === tier);
  if (withKey) {
    return withKey;
  }
  if (want === HOST_DEFAULT_PRODUCT_KEY) {
    return (
      plans.find((p) => p.tier === tier && (p.billingPeriod ?? 'MONTH') === billing && !p.subscriptionProductKey) ??
      plans.find((p) => p.tier === tier && !p.subscriptionProductKey)
    );
  }
  return undefined;
}

function setAdditionalQuotaExtra(
  units: { quotaKey: string; quantity: number }[] | undefined,
  quotaKey: string,
  nextExtra: number,
): { quotaKey: string; quantity: number }[] {
  const map = new Map((units ?? []).map((u) => [u.quotaKey, u.quantity]));
  if (nextExtra <= 0) {
    map.delete(quotaKey);
  } else {
    map.set(quotaKey, nextExtra);
  }
  return [...map.entries()].map(([k, quantity]) => ({ quotaKey: k, quantity }));
}

function extraUnitsFor(units: { quotaKey: string; quantity: number }[] | undefined, quotaKey: string): number {
  return units?.find((u) => u.quotaKey === quotaKey)?.quantity ?? 0;
}

function pickSubscriptionRenewalRaw(inst: OrganizationSubscriptionInstanceDto | undefined): unknown {
  if (!inst) {
    return undefined;
  }
  return inst.periodEnd ?? inst.validUntil;
}

/**
 * Show charge/credit split when the cart totals include a mid-cycle breakdown. Requiring credit greater than 0
 * hid monthly (same billing cadence) downgrades where credit can round to €0.00 while the new-plan
 * portion is still positive.
 */
function shouldShowProrataChargeCreditSplit(planLineTotal: SubscriptionCheckoutCartPlanLineTotal | undefined): boolean {
  if (!planLineTotal) {
    return false;
  }
  const credit = planLineTotal.estimatedCreditForUnusedTimeEur;
  const newPortion = planLineTotal.estimatedNewPlanPortionEur;
  if (typeof credit !== 'number' || typeof newPortion !== 'number') {
    return false;
  }
  if (credit > 1e-6 || newPortion > 1e-6) {
    return true;
  }
  const amt = planLineTotal.amountEur;
  return amt > 1e-6 && Math.abs(newPortion - credit - amt) < 0.021;
}

function parseSubscriptionDateField(raw: unknown): Date | undefined {
  if (raw == null) {
    return undefined;
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw;
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s.length) {
      return undefined;
    }
    try {
      const d = parseISO(s);
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    } catch {
      /* fall through */
    }
    const d2 = new Date(s);
    return Number.isNaN(d2.getTime()) ? undefined : d2;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

/**
 * Next renewal instant for checkout UX: prefers `periodEnd` / `validUntil`, then one billing period after
 * `periodStart`, then one period from today (many seeded free rows omit explicit period bounds).
 */
function resolveCheckoutLineRenewalAsDate(
  subInst: OrganizationSubscriptionInstanceDto | undefined,
  parentInst: OrganizationSubscriptionInstanceDto | undefined,
  isAddonRow: boolean,
  lineBilling: SubscriptionBillingPeriod,
): Date {
  const cadence = lineBilling === 'YEAR' ? 'YEAR' : 'MONTH';
  const bump = (from: Date) => (cadence === 'YEAR' ? addYears(from, 1) : addMonths(from, 1));

  const fromInstance = (inst: OrganizationSubscriptionInstanceDto | undefined): Date | undefined => {
    const raw = pickSubscriptionRenewalRaw(inst);
    const end = parseSubscriptionDateField(raw);
    if (end) {
      return end;
    }
    const ps = parseSubscriptionDateField(inst?.periodStart);
    if (ps) {
      return bump(ps);
    }
    return undefined;
  };

  return fromInstance(subInst) ?? (isAddonRow ? fromInstance(parentInst) : undefined) ?? bump(new Date());
}

function resolveCheckoutLineRenewalDisplayDate(
  subInst: OrganizationSubscriptionInstanceDto | undefined,
  parentInst: OrganizationSubscriptionInstanceDto | undefined,
  isAddonRow: boolean,
  lineBilling: SubscriptionBillingPeriod,
): string {
  return formatSubscriptionBillingInstantUtcMedium(
    resolveCheckoutLineRenewalAsDate(subInst, parentInst, isAddonRow, lineBilling),
  );
}

/**
 * Last inclusive **UTC** service day for proration footnote copy (`periodEnd` / renewal instant is excluded).
 */
function formatInclusiveLastServiceDayForProrataFootnote(
  subInst: OrganizationSubscriptionInstanceDto | undefined,
  parentInst: OrganizationSubscriptionInstanceDto | undefined,
  isAddonRow: boolean,
  lineBilling: SubscriptionBillingPeriod,
): string {
  const strictEnd = (inst: OrganizationSubscriptionInstanceDto | undefined): Date | undefined =>
    parseSubscriptionDateField(pickSubscriptionRenewalRaw(inst));

  const sub = strictEnd(subInst);
  if (sub) {
    return formatInclusiveLastServiceDayFromExclusiveEndUtc(sub);
  }
  if (isAddonRow) {
    const parent = strictEnd(parentInst);
    if (parent) {
      return formatInclusiveLastServiceDayFromExclusiveEndUtc(parent);
    }
  }
  return formatInclusiveLastServiceDayFromExclusiveEndUtc(
    resolveCheckoutLineRenewalAsDate(subInst, parentInst, isAddonRow, lineBilling),
  );
}

function additionalQuotaUnitsEqual(
  a: { quotaKey: string; quantity: number }[] | undefined,
  b: { quotaKey: string; quantity: number }[] | undefined,
): boolean {
  const toMap = (x: typeof a) => new Map((x ?? []).map((u) => [u.quotaKey, u.quantity]));
  const ma = toMap(a);
  const mb = toMap(b);
  if (ma.size !== mb.size) {
    return false;
  }
  for (const [k, v] of ma) {
    if (mb.get(k) !== v) {
      return false;
    }
  }
  return true;
}

function effectiveCheckoutLineTier(
  line: SubscriptionCheckoutCartLineDto,
  productLine: CatalogProductLine | undefined,
): SubscriptionTier | undefined {
  const raw = line.tier?.trim();
  if (raw) {
    return raw as SubscriptionTier;
  }
  return soleTierForProductLine(productLine);
}

function toCheckoutCartPatchLine(
  l: SubscriptionCheckoutCartLineDto,
  productLineByKey: Map<string, CatalogProductLine>,
): NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']>[number] {
  const pk = l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const pl = productLineByKey.get(pk);
  const tier = effectiveCheckoutLineTier(l, pl);
  return {
    subscriptionProductKey: l.subscriptionProductKey,
    ...(tier ? { tier } : {}),
    billingPeriod: l.billingPeriod as SubscriptionBillingPeriod | undefined,
    additionalQuotaUnits: l.additionalQuotaUnits,
    ...(l.parentSubscriptionInstanceId ? { parentSubscriptionInstanceId: l.parentSubscriptionInstanceId } : {}),
  };
}

/** Main cart line matches the org’s booked row for that product (tier, billing, quotas). */
function isCheckoutMainLineUnchangedVsOrg(
  org: OrganizationDto | undefined,
  line: SubscriptionCheckoutCartLineDto,
  productLine: CatalogProductLine | undefined,
): boolean {
  const pk = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const inst = findOrganizationInstanceForProduct(org, pk);
  if (!inst?.subscriptionPlan) {
    return false;
  }
  const instTier = (inst.tier ?? inst.subscriptionPlan.tier) as string;
  const instBp = (inst.billingPeriod ?? inst.subscriptionPlan.billingPeriod ?? 'MONTH') as string;
  const lineTier = effectiveCheckoutLineTier(line, productLine);
  if (!lineTier || instTier !== lineTier) {
    return false;
  }
  if (instBp !== (line.billingPeriod ?? 'MONTH')) {
    return false;
  }
  return additionalQuotaUnitsEqual(inst.additionalQuotaUnits, line.additionalQuotaUnits);
}

function categorySortRank(cat: string): number {
  const i = SUBSCRIPTION_CATALOG_CATEGORY_ORDER.indexOf(cat as (typeof SUBSCRIPTION_CATALOG_CATEGORY_ORDER)[number]);
  return i === -1 ? 999 : i;
}

function cartLineIsAddon(
  line: SubscriptionCheckoutCartLineDto,
  linePlan: SubscriptionPlan | undefined,
  catalogProductLineByKey: Map<string, CatalogProductLine>,
): boolean {
  const pk = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const pline = catalogProductLineByKey.get(pk);
  return pline?.isAddon ?? linePlan?.requiresParent === true;
}

function parentProductKeyForAddonLine(linePlan: SubscriptionPlan): string {
  return (linePlan.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) as string;
}

/**
 * Main lines first (category order, then product key); each main immediately followed by its add-ons
 * (`parentProductKey`). Remaining add-ons append at the end. Indices reference `cart.lines` / totals order.
 */
function buildOrderedCheckoutLineIndices(
  cartLines: SubscriptionCheckoutCartLineDto[],
  cartLinePlans: (SubscriptionPlan | undefined)[],
  catalogProductLineByKey: Map<string, CatalogProductLine>,
): number[] {
  const valid = cartLines
    .map((_, i) => i)
    .filter((i) => {
      const line = cartLines[i];
      const plan = cartLinePlans[i];
      return Boolean(line && plan);
    });

  type Meta = { i: number; isAddon: boolean; parentPk: string | null; cat: string; pk: string };
  const metas: Meta[] = valid.map((i) => {
    const line = cartLines[i];
    const plan = cartLinePlans[i]!;
    const pk = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
    const isAddon = cartLineIsAddon(line, plan, catalogProductLineByKey);
    const parentPk = isAddon ? parentProductKeyForAddonLine(plan) : null;
    const cat = subscriptionCategoryKey(plan);
    return { i, isAddon, parentPk, cat, pk };
  });

  const mains = metas
    .filter((m) => !m.isAddon)
    .sort((a, b) => categorySortRank(a.cat) - categorySortRank(b.cat) || a.pk.localeCompare(b.pk));
  const addons = metas.filter((m) => m.isAddon).sort((a, b) => a.pk.localeCompare(b.pk));

  const out: number[] = [];
  const usedAddon = new Set<number>();
  for (const m of mains) {
    out.push(m.i);
    for (const a of addons) {
      if (a.parentPk === m.pk && !usedAddon.has(a.i)) {
        out.push(a.i);
        usedAddon.add(a.i);
      }
    }
  }
  for (const a of addons) {
    if (!usedAddon.has(a.i)) {
      out.push(a.i);
    }
  }
  return out;
}

const createBillingAddress = (
  orgBillingAddress: Address | undefined,
  formData?: EditActiveOrganization['billingAddress'],
): Address =>
  ({
    ...(orgBillingAddress || {}),
    ...(formData || {}),
    type: ADDRESS_TYPE.BILLING,
  }) as Address;

const createFormDefaults = (
  activeOrganization: OrganizationDto | undefined,
  billingAddress: Address | undefined,
): EditActiveOrganization => ({
  general: { name: activeOrganization?.name || 'no name' },
  billingAddress: {
    name: billingAddress?.name || '',
    address1: billingAddress?.address1 || '',
    address2: billingAddress?.address2 || '',
    city: billingAddress?.city || '',
    country: billingAddress?.country || '',
    postCode: billingAddress?.postCode || '',
    vat: billingAddress?.vat || '',
  },
});

/** Skip tooltips below `sm` (full-screen checkout) — touch UAs show them awkwardly. */
function CheckoutActionTooltip({
  wideScreen,
  title,
  children,
}: {
  wideScreen: boolean;
  title: string;
  children: ReactElement;
}) {
  if (!wideScreen) {
    return children;
  }
  return <Tooltip title={title}>{children}</Tooltip>;
}

export default function OrganizationSubscriptionCheckoutModal({
  open,
  onClose,
  organizationId,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const isWideScreen = useBreakpointUp('sm');
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  /** Which cart line’s product key is open in ManagePlanDialog (multi-line merge). */
  const [editingProductKey, setEditingProductKey] = useState<string | null>(null);
  const [taxClassification, setTaxClassification] = useState<TaxClassification>();

  const { data: checkoutCart, isPending: cartLoading } = useSubscriptionCheckoutCartQuery(
    open ? organizationId : undefined,
  );
  const { data: org } = useGetOrganizationQuery(open ? organizationId : undefined);
  const { data: quotaBalances } = useQuotaBalances(
    open ? 'Organization' : undefined,
    open ? organizationId : undefined,
  );
  const { data: subscriptionPlans } = useSubscriptionPlansQuery({
    entityType: 'Organization',
    enabled: open,
  });

  const patchCart = usePatchOrganizationSubscriptionCheckoutCart();
  const checkoutMutation = useCheckoutSubscriptionCart();
  const { mutateAsync: taxClassificationMutation } = useTaxClassificationMutation();
  const { mutateAsync: editOrganizationMutation, isPending: editOrgPending } = useEditOrganizationMutation();
  const { data: permissions } = usePermissionsQuery();
  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');

  const orgPerms = organizationId ? permissions?.organizations?.[organizationId] : undefined;
  const canCompleteCheckout =
    !!orgPerms?.includes(ORGANIZATION_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN) ||
    !!orgPerms?.includes(ORGANIZATION_PERMISSIONS.COMPLETE_SUBSCRIPTION_CHECKOUT);

  const cart = checkoutCart?.cart;
  const totals = checkoutCart?.totals;
  const hasInvoice = checkoutCart?.hasInvoice ?? false;
  const planChange = parseCheckoutCartPlanChange(getEffectivePlanChangeRaw(cart ?? null));

  const cartLines: SubscriptionCheckoutCartLineDto[] = cart?.lines ?? [];

  const catalogPlans = useMemo(() => subscriptionPlans?.plans ?? [], [subscriptionPlans?.plans]);

  /** For checkout line actions: only show “edit plan” when the product has more than one tier (same as plan page). */
  const catalogProductLineByKey = useMemo(() => {
    const m = new Map<string, CatalogProductLine>();
    const grouped = groupOrganizationProductLines(catalogPlans);
    for (const [, block] of grouped) {
      for (const pl of [...block.main, ...block.addOn]) {
        m.set(pl.subscriptionProductKey, pl);
      }
    }
    return m;
  }, [catalogPlans]);

  const planChangeForManageDialog = useMemo(() => {
    if (editingProductKey) {
      const l = cartLines.find((c) => c.subscriptionProductKey === editingProductKey);
      if (l) {
        const pk = editingProductKey.trim() || HOST_DEFAULT_PRODUCT_KEY;
        const productLine = catalogProductLineByKey.get(pk);
        const tier = effectiveCheckoutLineTier(l, productLine);
        if (tier) {
          return {
            tier,
            billingPeriod: l.billingPeriod as SubscriptionBillingPeriod | undefined,
            additionalQuotaUnits: l.additionalQuotaUnits,
          };
        }
      }
    }
    return planChange ?? null;
  }, [editingProductKey, cartLines, planChange, catalogProductLineByKey]);

  /** Primary booked instance on the org — same source for pricing “Current plan” and ManagePlanDialog “current tier” (never the cart draft). */
  const bookedPlanForPricingDisplay = useMemo((): OrganizationBookedPlanSummary | null => {
    const inst = getPrimaryOrganizationSubscriptionInstance(org);
    if (!inst) {
      return null;
    }
    const plan = inst.subscriptionPlan as SubscriptionPlan | undefined;
    const tier = (inst.tier ?? plan?.tier ?? 'FREE') as string;
    const subscriptionProductKey = inst.subscriptionProductKey ?? HOST_DEFAULT_PRODUCT_KEY;
    const billingPeriod = inst.billingPeriod ?? plan?.billingPeriod;
    return { tier, subscriptionProductKey, billingPeriod };
  }, [org]);

  /** Actually subscribed tier/billing — passed to ManagePlanDialog so “CURRENT PLAN” matches the plan page, not the checkout line being edited. */
  const manageDialogBookedTier = (bookedPlanForPricingDisplay?.tier ?? 'FREE') as SubscriptionTier;
  const manageDialogBookedBillingPeriod = (bookedPlanForPricingDisplay?.billingPeriod ??
    'MONTH') as SubscriptionBillingPeriod;

  const cartLinePlans = useMemo(() => {
    if (!catalogPlans.length || !cartLines.length) {
      return [] as (SubscriptionPlan | undefined)[];
    }
    return cartLines.map((line) => {
      const pk = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
      const productLine = catalogProductLineByKey.get(pk);
      const tier = effectiveCheckoutLineTier(line, productLine);
      if (!tier) {
        return undefined;
      }
      const bp = (line.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
      const picked = pickPlanForProductLine(catalogPlans, line.subscriptionProductKey, tier, bp);
      const embeddedForLine = org?.subscriptions?.find(
        (s) => s.subscriptionProductKey === line.subscriptionProductKey && s.entityType === 'Organization',
      )?.subscriptionPlan as SubscriptionPlan | undefined;
      return picked
        ? normalizeSubscriptionPlanOverage(mergeOverageFromMatchingSubscriptionSnapshot(picked, embeddedForLine))
        : undefined;
    });
  }, [catalogPlans, cartLines, org?.subscriptions, catalogProductLineByKey]);

  const checkoutOrderedLineIndices = useMemo(
    () => buildOrderedCheckoutLineIndices(cartLines, cartLinePlans, catalogProductLineByKey),
    [cartLines, cartLinePlans, catalogProductLineByKey],
  );

  /** Sum of full-period recurring amounts per billing cadence, with earliest next charge in each group. */
  const recurringBillingSummaries = useMemo(() => {
    if (!org || checkoutOrderedLineIndices.length === 0) {
      return {
        annual: null as null | { total: number; next: string },
        monthly: null as null | { total: number; next: string },
      };
    }
    let annualTotal = 0;
    let monthlyTotal = 0;
    const annualDates: Date[] = [];
    const monthlyDates: Date[] = [];

    for (const lineIndex of checkoutOrderedLineIndices) {
      const line = cartLines[lineIndex];
      const linePlan = cartLinePlans[lineIndex];
      if (!line || !linePlan) {
        continue;
      }
      const planLineTotal = totals?.planLines?.[lineIndex];
      const productKeyNorm = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
      const productLine = catalogProductLineByKey.get(productKeyNorm);
      const effectiveTier = effectiveCheckoutLineTier(line, productLine);
      if (!effectiveTier) {
        continue;
      }
      const isAddonRow = cartLineIsAddon(line, linePlan, catalogProductLineByKey);
      const parentPkForAddon = linePlan.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
      let subInstForRenewal = findOrganizationInstanceForProduct(org, productKeyNorm);
      if (!subInstForRenewal && productKeyNorm === HOST_DEFAULT_PRODUCT_KEY) {
        subInstForRenewal = getPrimaryOrganizationSubscriptionInstance(org);
      }
      const parentInstForRenewal = isAddonRow ? findOrganizationInstanceForProduct(org, parentPkForAddon) : undefined;

      const bp = (line.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
      const recurring = computeOrganizationRenewalAmount(linePlan, line.additionalQuotaUnits);
      if (typeof recurring !== 'number' || Number.isNaN(recurring)) {
        continue;
      }
      let nextD = resolveCheckoutLineRenewalAsDate(subInstForRenewal, parentInstForRenewal, isAddonRow, bp);
      if (planLineTotal?.nextRenewalDisplayIso != null) {
        const fromIso = parseISO(planLineTotal.nextRenewalDisplayIso);
        if (!Number.isNaN(fromIso.getTime())) {
          nextD = fromIso;
        }
      }
      if (bp === 'YEAR') {
        annualTotal += recurring;
        annualDates.push(nextD);
      } else {
        monthlyTotal += recurring;
        monthlyDates.push(nextD);
      }
    }

    const earliest = (dates: Date[]) => dates.reduce((a, b) => (a.getTime() <= b.getTime() ? a : b));

    const annual =
      annualTotal > 0 && annualDates.length > 0
        ? {
            total: Math.round(annualTotal * 100) / 100,
            next: format(earliest(annualDates), 'MMM d, yyyy'),
          }
        : null;
    const monthly =
      monthlyTotal > 0 && monthlyDates.length > 0
        ? {
            total: Math.round(monthlyTotal * 100) / 100,
            next: format(earliest(monthlyDates), 'MMM d, yyyy'),
          }
        : null;

    return { annual, monthly };
  }, [checkoutOrderedLineIndices, cartLines, cartLinePlans, org, catalogProductLineByKey, totals?.planLines]);

  const currentActiveOrganizationBillingAddress = useMemo(
    () => activeOrganization?.addresses.find((address) => address.type === ADDRESS_TYPE.BILLING),
    [activeOrganization?.addresses],
  );

  const canEditBillingAddress =
    (!hasInvoice &&
      !!activeOrganization?.id &&
      permissions?.organizations?.[activeOrganization.id]?.includes('update')) ||
    false;

  const editActiveOrganizationSchema = editActiveOrganizationSchemaFactory(t);
  const form = useForm<EditActiveOrganization>({
    resolver: zodResolver(editActiveOrganizationSchema),
    values: createFormDefaults(activeOrganization, currentActiveOrganizationBillingAddress),
  });

  useEffect(() => {
    (async () => {
      const country = currentActiveOrganizationBillingAddress?.country;
      if (!country) {
        return;
      }
      const countryCode = COUNTRY_TO_CODE_MAP[country as keyof typeof COUNTRY_TO_CODE_MAP];
      if (!countryCode) return;
      try {
        const result = await taxClassificationMutation({ countryCode });
        setTaxClassification(result.taxClassification);
      } catch {
        setTaxClassification(undefined);
      }
    })();
  }, [currentActiveOrganizationBillingAddress?.country, taxClassificationMutation]);

  const latestOrderBillingCountry =
    currentActiveOrganizationBillingAddress?.country && currentActiveOrganizationBillingAddress.country !== ''
      ? currentActiveOrganizationBillingAddress.country
      : undefined;

  const vatCountryDisplay = useMemo(() => {
    const vat = currentActiveOrganizationBillingAddress?.vat;
    if (!latestOrderBillingCountry) {
      return t('common.N/A');
    }
    if (taxClassification === 'intraCommunity' && vat && vat !== '') {
      return t('event.checkoutModal.reverseCharge');
    }
    if (taxClassification === 'thirdPartyCountry') {
      return t('event.checkoutModal.thirdPartyCountry');
    }
    return capitalize(latestOrderBillingCountry);
  }, [currentActiveOrganizationBillingAddress?.vat, latestOrderBillingCountry, taxClassification, t]);

  const billingAddressReset = useCallback(() => {
    form.reset(createFormDefaults(activeOrganization, currentActiveOrganizationBillingAddress));
  }, [form, currentActiveOrganizationBillingAddress, activeOrganization]);

  useEffect(() => {
    billingAddressReset();
    setIsEditingBillingAddress(false);
  }, [currentActiveOrganizationBillingAddress, billingAddressReset]);

  useEffect(() => {
    if (!open) {
      setPlanModalOpen(false);
      setEditingProductKey(null);
    }
  }, [open]);

  const billingAddressOnSubmit = async (data: EditActiveOrganization) => {
    if (!activeOrganization) {
      return;
    }

    const billingAddress = createBillingAddress(currentActiveOrganizationBillingAddress, data.billingAddress);

    const updateData: PatchOrganizationDto = {
      name: data.general.name,
      addresses: [],
    };

    if (
      billingAddress?.name &&
      billingAddress?.address1 &&
      billingAddress?.city &&
      billingAddress?.country &&
      billingAddress?.postCode
    ) {
      updateData.addresses.push({
        name: billingAddress.name,
        address1: billingAddress.address1,
        address2: billingAddress.address2,
        city: billingAddress.city,
        postCode: billingAddress.postCode,
        country: billingAddress.country,
        vat: billingAddress.vat,
        type: ADDRESS_TYPE.BILLING,
      });
    }

    try {
      await editOrganizationMutation({
        id: activeOrganization.id,
        data: updateData,
      });
      setActiveOrganization({
        ...activeOrganization,
        addresses: updateData.addresses,
      });
      setIsEditingBillingAddress(false);
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.updateOrganizationBillingAddressError'));
    }
  };

  const handleClose = () => {
    onClose();
    billingAddressReset();
  };

  const patchPending = patchCart.isPending;
  const hasPendingCartMutation = patchPending;

  const totalEur = totals?.totalEur ?? 0;
  const hasAmount = totalEur > 0;

  const handlePlanQuotaExtraChange = async (lineIndex: number, quotaKey: string, nextExtra: number) => {
    const rawLines = cart?.lines ?? [];
    const line = rawLines[lineIndex];
    const planAt = cartLinePlans[lineIndex];
    if (!line || !planAt) {
      return;
    }
    const nextUnits = setAdditionalQuotaExtra(line.additionalQuotaUnits, quotaKey, nextExtra);
    const updated = rawLines.map((l, idx) => (idx === lineIndex ? { ...l, additionalQuotaUnits: nextUnits } : l));
    const linesBody: NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']> = updated.map((l) =>
      toCheckoutCartPatchLine(l, catalogProductLineByKey),
    );
    try {
      await patchCart.mutateAsync({
        organizationId,
        body: {
          lines: linesBody,
          quotaAddons: cart?.quotaAddons ?? [],
        },
      });
    } catch {
      notification.notifyError(t('subscription.organization.planPage.addonError'));
    }
  };

  const handlePlanBillingPeriodChange = async (lineIndex: number, nextBp: SubscriptionBillingPeriod) => {
    const rawLines = cart?.lines ?? [];
    const line = rawLines[lineIndex];
    const linePlan = cartLinePlans[lineIndex];
    if (!line || !linePlan) {
      return;
    }
    const prevBp = (line.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
    let updated = rawLines.map((l, idx) => (idx === lineIndex ? { ...l, billingPeriod: nextBp } : l));

    const editedParentProductKey = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
    const isParentProductLine = !linePlan.requiresParent;
    if (isParentProductLine && prevBp === 'MONTH' && nextBp === 'YEAR') {
      updated = updated.map((l, j) => {
        const lpJ = cartLinePlans[j];
        if (!lpJ?.requiresParent) {
          return l;
        }
        const ppk = lpJ.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
        if (ppk === editedParentProductKey) {
          return { ...l, billingPeriod: 'YEAR' as SubscriptionBillingPeriod };
        }
        return l;
      });
    } else if (isParentProductLine && prevBp === 'YEAR' && nextBp === 'MONTH') {
      updated = updated.map((l, j) => {
        const lpJ = cartLinePlans[j];
        if (!lpJ?.requiresParent) {
          return l;
        }
        const ppk = lpJ.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
        if (ppk === editedParentProductKey) {
          return { ...l, billingPeriod: 'MONTH' as SubscriptionBillingPeriod };
        }
        return l;
      });
    }

    const linesBody: NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']> = updated.map((l) =>
      toCheckoutCartPatchLine(l, catalogProductLineByKey),
    );
    try {
      await patchCart.mutateAsync({
        organizationId,
        body: {
          lines: linesBody,
          quotaAddons: cart?.quotaAddons ?? [],
        },
      });
    } catch {
      notification.notifyError(t('subscription.organization.planPage.addonError'));
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps -- one-shot cart repair when parent is monthly but add-on line is yearly */
  useEffect(() => {
    if (!open || cartLoading || !cart?.lines?.length || !cartLinePlans.length) {
      return;
    }
    void (async () => {
      for (let idx = 0; idx < cartLines.length; idx++) {
        const line = cartLines[idx];
        const lp = cartLinePlans[idx];
        if (!line || !lp?.requiresParent) {
          continue;
        }
        const parentPk = lp.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
        const parentBp = resolveParentBillingPeriodForAddonFromOrgAndCart(org, cartLines, parentPk);
        if (parentBp === 'MONTH' && (line.billingPeriod ?? 'MONTH') === 'YEAR') {
          await handlePlanBillingPeriodChange(idx, 'MONTH');
          break;
        }
      }
    })();
  }, [open, cartLoading, checkoutCart?.cart?.updatedAt, organizationId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleRemovePlanLine = async (lineIndex: number) => {
    const rawLines = cart?.lines ?? [];
    const nextLines = rawLines.filter((_, i) => i !== lineIndex);
    const qa = cart?.quotaAddons ?? [];
    try {
      if (nextLines.length === 0 && qa.length === 0) {
        await patchCart.mutateAsync({ organizationId, body: { clear: true } });
        return;
      }
      if (nextLines.length === 0) {
        await patchCart.mutateAsync({
          organizationId,
          body: { lines: [], quotaAddons: qa },
        });
        return;
      }
      const linesBody: NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']> = nextLines.map((l) =>
        toCheckoutCartPatchLine(l, catalogProductLineByKey),
      );
      await patchCart.mutateAsync({
        organizationId,
        body: {
          lines: linesBody,
          quotaAddons: qa,
        },
      });
    } catch {
      notification.notifyError(t('subscription.organization.planPage.addonError'));
    }
  };

  const handleProceed = async () => {
    if (!canCompleteCheckout || !activeOrganization?.id || !hasMonetizeAccess || monetizeAccessLoading) {
      return;
    }
    try {
      const res = await checkoutMutation.mutateAsync({
        organizationId,
        redirectUrlAfterPayment: window.location.href,
      });
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch (error) {
      const errorMessage = t('event.checkoutModal.notification.requestOrderPaymentError');
      const errorCode = (error as ServerError).errorCode;
      const reason = errorCode ? ` (${t(`error.${errorCode}` as TranslationKey)})` : '';
      notification.notifyError(`${errorMessage}${reason}`);
    }
  };

  const compactBtnSx = {
    width: 28,
    height: 28,
    p: 0.25,
    minWidth: 28,
  };

  return (
    <>
      <Modal
        open={open}
        size={isWideScreen ? 'lg' : 'sm'}
        width={isWideScreen ? 1000 : undefined}
        minHeight={isWideScreen ? 360 : undefined}
        maxHeight={isWideScreen ? 'min(92vh, calc(100vh - 48px))' : undefined}
        sx={isWideScreen ? { overflow: 'hidden' } : undefined}
        fullScreen={!isWideScreen}
        data-testid="organization-subscription-checkout-modal"
        handleClose={handleClose}
        title={t('common.checkout')}
        description={t(
          canCompleteCheckout
            ? 'subscription.organization.planPage.subscriptionCheckoutModalDescription'
            : 'subscription.organization.planPage.subscriptionCheckoutModalDescriptionRequestOnly',
        )}
        contentSx={
          isWideScreen
            ? { minHeight: 0 }
            : {
                overflow: 'auto',
                minHeight: 0,
              }
        }
        footer={
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              aria-label={t('subscription.organization.planPage.subscriptionCheckoutBackToPlansAria')}
            >
              {t('subscription.organization.planPage.subscriptionCheckoutBackToPlans')}
            </Button>
            <Button
              onClick={() => void handleProceed()}
              variant="contained"
              aria-label="Proceed"
              loading={checkoutMutation.isPending}
              disabled={
                !canCompleteCheckout ||
                !hasAmount ||
                cartLoading ||
                !currentActiveOrganizationBillingAddress ||
                !hasMonetizeAccess ||
                monetizeAccessLoading
              }
            >
              {t('common.proceed')}
            </Button>
          </>
        }
      >
        {cartLoading && !checkoutCart ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ...(isWideScreen ? { flex: 1, minHeight: 0 } : {}),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                flex: { xs: 'none', sm: 1 },
                minHeight: { xs: 'auto', sm: 0 },
                maxHeight: { xs: 'none', sm: '100%' },
                overflow: { xs: 'visible', sm: 'hidden' },
              }}
            >
              <Box
                sx={{
                  /* Match summary column flex so space splits 50/50 until summary hits maxWidth. */
                  flex: { xs: 'none', sm: '1 1 0%' },
                  minWidth: 0,
                  minHeight: { xs: 'auto', sm: 0 },
                  overflowY: { xs: 'visible', sm: 'auto' },
                  pr: { sm: 0.5 },
                }}
              >
                {checkoutOrderedLineIndices.map((lineIndex, orderPos) => {
                  const line = cartLines[lineIndex];
                  const linePlan = cartLinePlans[lineIndex];
                  if (!line || !linePlan) {
                    return null;
                  }
                  const productKeyNorm = line.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
                  const productLine = catalogProductLineByKey.get(productKeyNorm);
                  const effectiveTier = effectiveCheckoutLineTier(line, productLine);
                  if (!effectiveTier) {
                    return null;
                  }
                  const planKeysAt = collectQuotaKeysFromPlan(linePlan);
                  const bp = (line.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
                  const recurringPriceDisplay = getCheckoutRecurringPriceDisplay(
                    (key) => t(key as TranslationKey),
                    linePlan,
                    bp,
                    line.additionalQuotaUnits,
                  );
                  const recurringFull = recurringPriceDisplay.recurringFull;
                  const recurringDisplayEur = recurringPriceDisplay.recurringDisplayEur;
                  const prorataToday =
                    totals?.planLines?.[lineIndex]?.amountEur ??
                    (cartLines.length === 1 && typeof totals?.planChangeAmountEur === 'number'
                      ? totals.planChangeAmountEur
                      : undefined);
                  const planLineTotal = totals?.planLines?.[lineIndex];
                  const showProrataChargeCreditSplit = shouldShowProrataChargeCreditSplit(planLineTotal);
                  const showEditPlanButton = productLine != null && uniqueTierCountForProductLine(productLine) > 1;
                  const isAddonRow = cartLineIsAddon(line, linePlan, catalogProductLineByKey);
                  const prevIdx = orderPos > 0 ? checkoutOrderedLineIndices[orderPos - 1] : undefined;
                  const prevIsAddon =
                    prevIdx !== undefined
                      ? cartLineIsAddon(cartLines[prevIdx]!, cartLinePlans[prevIdx], catalogProductLineByKey)
                      : false;
                  const showAddonsSectionHeading = isAddonRow && !prevIsAddon;
                  const displayName =
                    resolveTranslatedProductField(
                      t,
                      'subscription.organization.planPage.productName',
                      productKeyNorm,
                    ) ?? productKeyNorm;
                  const claimText = resolveProductClaimText(t, productKeyNorm, effectiveTier, {
                    allowPlanWideClaim: false,
                  });
                  const parentPkForAddon = linePlan.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
                  const parentBillingCadence = isAddonRow
                    ? resolveParentBillingPeriodForAddonFromOrgAndCart(org, cartLines, parentPkForAddon)
                    : undefined;
                  const addonCannotSelectAnnual = isAddonRow && parentBillingCadence === 'MONTH';
                  const canSwitchBillingPeriod =
                    productLine != null &&
                    !addonCannotSelectAnnual &&
                    plansForBillingWindow(productLine, 'MONTH').some((p) => p.tier === effectiveTier) &&
                    plansForBillingWindow(productLine, 'YEAR').some((p) => p.tier === effectiveTier);
                  let subInstForRenewal = findOrganizationInstanceForProduct(org, productKeyNorm);
                  if (!subInstForRenewal && productKeyNorm === HOST_DEFAULT_PRODUCT_KEY) {
                    subInstForRenewal = getPrimaryOrganizationSubscriptionInstance(org);
                  }
                  const parentInstForRenewal = isAddonRow
                    ? findOrganizationInstanceForProduct(org, parentPkForAddon)
                    : undefined;
                  const periodStartParsed =
                    parseSubscriptionDateField(subInstForRenewal?.periodStart) ??
                    (isAddonRow ? parseSubscriptionDateField(parentInstForRenewal?.periodStart) : undefined);
                  const periodEndParsed =
                    parseSubscriptionDateField(pickSubscriptionRenewalRaw(subInstForRenewal)) ??
                    (isAddonRow
                      ? parseSubscriptionDateField(pickSubscriptionRenewalRaw(parentInstForRenewal))
                      : undefined);
                  const renewalFormatted = resolveCheckoutLineRenewalDisplayDate(
                    subInstForRenewal,
                    parentInstForRenewal,
                    isAddonRow,
                    bp,
                  );
                  const renewalFormattedForDisplay =
                    planLineTotal?.nextRenewalDisplayIso != null
                      ? formatSubscriptionBillingInstantUtcMedium(parseISO(planLineTotal.nextRenewalDisplayIso))
                      : renewalFormatted;
                  const prorataFootnotePeriodEndLabel = formatInclusiveLastServiceDayForProrataFootnote(
                    subInstForRenewal,
                    parentInstForRenewal,
                    isAddonRow,
                    bp,
                  );
                  const hidePricingRow = !isAddonRow && isCheckoutMainLineUnchangedVsOrg(org, line, productLine);
                  const chargePeriodStartLabel =
                    periodStartParsed != null
                      ? formatSubscriptionBillingInstantUtcMedium(periodStartParsed)
                      : format(new Date(), 'MMM d, yyyy');
                  const prorationWindowFromDisplay =
                    periodStartParsed != null
                      ? formatSubscriptionBillingInstantUtcMedium(periodStartParsed)
                      : planLineTotal?.prorationWindowStartIso != null
                        ? formatSubscriptionBillingInstantUtcMedium(parseISO(planLineTotal.prorationWindowStartIso))
                        : chargePeriodStartLabel;
                  const prorationWindowToDisplay =
                    periodEndParsed != null
                      ? formatInclusiveLastServiceDayFromExclusiveEndUtc(periodEndParsed)
                      : planLineTotal?.prorationWindowEndIso != null
                        ? formatInclusiveLastServiceDayFromExclusiveEndUtc(
                            parseISO(planLineTotal.prorationWindowEndIso),
                          )
                        : prorataFootnotePeriodEndLabel;
                  const isProratedDueToday =
                    !hidePricingRow &&
                    typeof prorataToday === 'number' &&
                    typeof recurringFull === 'number' &&
                    !Number.isNaN(prorataToday) &&
                    !Number.isNaN(recurringFull) &&
                    Math.round(prorataToday * 100) !== Math.round(recurringFull * 100);
                  const showTierInTitle = productLine != null && uniqueTierCountForProductLine(productLine) > 1;
                  const tierTitle = showTierInTitle
                    ? t(`subscription.organization.tiers.${effectiveTier}` as TranslationKey)
                    : '';

                  return (
                    <Box key={`${line.subscriptionProductKey}-${lineIndex}-${effectiveTier}-${bp}`}>
                      {showAddonsSectionHeading ? (
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, ml: 4 }}
                        >
                          {t('subscription.organization.planPage.subscriptionCheckoutAddons')}
                        </Typography>
                      ) : null}
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mb: 2,
                          ...(isAddonRow ? { ml: 3 } : {}),
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: claimText ? 1.5 : 1 }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="h6" component="h3" sx={{ minWidth: 0, m: 0 }}>
                              {displayName}
                              {showTierInTitle ? (
                                <Typography
                                  component="span"
                                  variant="h6"
                                  color="text.secondary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {` | ${tierTitle}`}
                                </Typography>
                              ) : null}
                            </Typography>
                            {claimText ? (
                              <Typography variant="body2" color="text.secondary" sx={{ m: 0, mt: 0 }}>
                                {claimText}
                              </Typography>
                            ) : null}
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                            {showEditPlanButton ? (
                              <CheckoutActionTooltip
                                wideScreen={isWideScreen}
                                title={t('subscription.organization.planPage.subscriptionCheckoutEditPlanTooltip')}
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label={t('subscription.organization.planPage.subscriptionCheckoutEditPlanAria')}
                                  disabled={patchPending}
                                  onClick={() => {
                                    setEditingProductKey(line.subscriptionProductKey);
                                    setPlanModalOpen(true);
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </CheckoutActionTooltip>
                            ) : null}
                            <CheckoutActionTooltip
                              wideScreen={isWideScreen}
                              title={t('subscription.organization.planPage.subscriptionCheckoutRemovePlanTooltip')}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  aria-label={t(
                                    'subscription.organization.planPage.subscriptionCheckoutRemovePlanAria',
                                  )}
                                  disabled={patchPending}
                                  onClick={() => void handleRemovePlanLine(lineIndex)}
                                  sx={{ color: 'action.active' }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </CheckoutActionTooltip>
                          </Stack>
                        </Stack>

                        {planKeysAt.map((quotaKey) => {
                          const unit = getOveragePricePerUnit(linePlan, quotaKey);
                          if (unit === undefined || !(unit > 0)) {
                            return null;
                          }
                          const included = includedQuantityForKey(linePlan, quotaKey);
                          const extra = extraUnitsFor(line.additionalQuotaUnits, quotaKey);
                          const total = included + extra;
                          const maxTotal = maxQuantityForKey(linePlan, quotaKey);
                          const atPlanMax = maxTotal !== undefined && total >= maxTotal;
                          const plusDisabled = patchPending || atPlanMax;
                          const canDecrease = extra > 0;
                          const minusTooltip = canDecrease
                            ? t('subscription.organization.planPage.quotaStepperPreviewDecrease')
                            : t('subscription.organization.planPage.quotaStepperPreviewAtMin', { min: included });
                          const bookedInst = findOrganizationInstanceForProduct(org, productKeyNorm);
                          const bookedPlanSnap = bookedInst?.subscriptionPlan as SubscriptionPlan | undefined;
                          const balanceForQuota = quotaBalances?.find((b) => b.quotaKey === quotaKey);
                          const bookedTotal =
                            bookedPlanSnap != null
                              ? getQuotaDisplayTotal(
                                  bookedPlanSnap,
                                  quotaKey,
                                  balanceForQuota,
                                  bookedInst?.additionalQuotaUnits,
                                )
                              : 0;
                          const billingPeriodEndDisplay = formatSubscriptionInstancePeriodEndDisplay(bookedInst);
                          const showDowngradeInfo =
                            Boolean(billingPeriodEndDisplay) && bookedInst && bookedTotal > total;
                          return (
                            <Box
                              key={`plan-q-${lineIndex}-${quotaKey}`}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mb: 2,
                                maxWidth: 300,
                                alignSelf: 'flex-start',
                                minWidth: 0,
                              }}
                            >
                              <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={0.5}
                                  sx={{ width: '100%', minWidth: 0 }}
                                >
                                  <CheckoutActionTooltip wideScreen={isWideScreen} title={minusTooltip}>
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        disabled={!canDecrease || patchPending}
                                        onClick={() =>
                                          void handlePlanQuotaExtraChange(lineIndex, quotaKey, Math.max(0, extra - 1))
                                        }
                                        aria-label={t('subscription.organization.planPage.quotaDecreaseAria')}
                                        sx={{
                                          border: 1,
                                          borderColor: 'divider',
                                          color: !canDecrease ? 'action.disabled' : 'text.secondary',
                                          ...compactBtnSx,
                                        }}
                                      >
                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </span>
                                  </CheckoutActionTooltip>
                                  <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                                    <Typography variant="caption" fontWeight={600} component="span" display="block">
                                      {formatQuotaStepperCountLine(t, quotaKey, total)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ fontSize: '0.65rem' }}
                                    >
                                      {formatQuotaStepperDeltaVsBooked(t, quotaKey, total, bookedTotal)}
                                    </Typography>
                                  </Box>
                                  <CheckoutActionTooltip
                                    wideScreen={isWideScreen}
                                    title={
                                      atPlanMax
                                        ? t('subscription.organization.planPage.quotaStepperAtPlanMax')
                                        : t('subscription.organization.planPage.quotaStepperPreviewIncrease')
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        type="button"
                                        size="small"
                                        color={plusDisabled ? 'default' : 'primary'}
                                        disabled={plusDisabled}
                                        onClick={() => void handlePlanQuotaExtraChange(lineIndex, quotaKey, extra + 1)}
                                        aria-label={t('subscription.organization.planPage.quotaIncreaseAria')}
                                        sx={{
                                          border: 1,
                                          borderColor: plusDisabled ? 'divider' : 'primary.main',
                                          color: plusDisabled ? 'action.disabled' : undefined,
                                          ...compactBtnSx,
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </span>
                                  </CheckoutActionTooltip>
                                </Stack>
                                {showDowngradeInfo && billingPeriodEndDisplay ? (
                                  <Alert severity="info" variant="outlined" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                    {formatQuotaDowngradeProceedAlert(
                                      t,
                                      quotaKey,
                                      bookedTotal - total,
                                      billingPeriodEndDisplay,
                                    )}
                                  </Alert>
                                ) : null}
                              </Stack>
                            </Box>
                          );
                        })}

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-start"
                          flexWrap="wrap"
                          gap={1}
                          sx={{ mt: planKeysAt.length ? 0.5 : 0, mb: 1 }}
                        >
                          {canSwitchBillingPeriod ? (
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={bp}
                              disabled={patchPending}
                              onChange={(_, v) => {
                                if (v) {
                                  void handlePlanBillingPeriodChange(lineIndex, v as SubscriptionBillingPeriod);
                                }
                              }}
                              aria-label={t('subscription.organization.planPage.switchBilling')}
                              sx={{
                                '& .MuiToggleButton-root': {
                                  fontSize: '0.8125rem',
                                  lineHeight: 1.2,
                                  py: 0.5,
                                  px: 1,
                                  minHeight: 28,
                                },
                              }}
                            >
                              <ToggleButton value="MONTH" size="small">
                                {t('subscription.organization.planPage.billingMonthly')}
                              </ToggleButton>
                              <ToggleButton value="YEAR" size="small">
                                {t('subscription.organization.planPage.billingAnnual')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                          ) : (
                            <Typography variant="body2" color="text.secondary" component="span">
                              {bp === 'YEAR'
                                ? t('subscription.organization.planPage.billedAnnually')
                                : t('subscription.organization.planPage.billedMonthly')}
                              {t('subscription.organization.planPage.subscriptionCheckoutRenewsOnComma', {
                                date: renewalFormattedForDisplay,
                              })}
                            </Typography>
                          )}
                          {canSwitchBillingPeriod ? (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {t('subscription.organization.planPage.subscriptionCheckoutRenewsOn', {
                                date: renewalFormattedForDisplay,
                              })}
                            </Typography>
                          ) : null}
                        </Stack>

                        {!hidePricingRow ? (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={2}
                            pt={0.5}
                            mt={0.25}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('subscription.organization.planPage.subscriptionCheckoutRecurringLabel')}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.25 }}>
                                <Typography component="span" variant="body1" fontWeight={600}>
                                  {recurringPriceDisplay.amountLabel}
                                </Typography>
                                {recurringDisplayEur !== undefined ? (
                                  <>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {recurringPriceDisplay.periodMid}
                                    </Typography>
                                    {recurringPriceDisplay.periodTail ? (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.4 }}
                                      >
                                        {recurringPriceDisplay.periodTail}
                                      </Typography>
                                    ) : null}
                                  </>
                                ) : null}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              {showProrataChargeCreditSplit ? (
                                <Stack spacing={0.75} alignItems="flex-end">
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {t(
                                        'subscription.organization.planPage.subscriptionCheckoutProratedNewPlanPortionLabel',
                                      )}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      €{planLineTotal!.estimatedNewPlanPortionEur!.toFixed(2)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {t('subscription.organization.planPage.subscriptionCheckoutProratedCreditLabel')}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>
                                      −€{planLineTotal!.estimatedCreditForUnusedTimeEur!.toFixed(2)}
                                    </Typography>
                                  </Box>
                                  <Divider flexItem sx={{ borderColor: 'divider' }} />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      {...(isProratedDueToday
                                        ? {
                                            'aria-label': t(
                                              'subscription.organization.planPage.subscriptionCheckoutProrataDueTodayAriaProrated',
                                            ),
                                          }
                                        : {})}
                                    >
                                      {t('subscription.organization.planPage.subscriptionCheckoutProratedNetDueLabel')}
                                      {isProratedDueToday ? '*' : ''}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                      {typeof prorataToday === 'number' ? `€${prorataToday.toFixed(2)}` : '—'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    {...(isProratedDueToday
                                      ? {
                                          'aria-label': t(
                                            'subscription.organization.planPage.subscriptionCheckoutProrataDueTodayAriaProrated',
                                          ),
                                        }
                                      : {})}
                                  >
                                    {t('subscription.organization.planPage.subscriptionCheckoutProrataLabel')}
                                    {isProratedDueToday ? '*' : ''}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600}>
                                    {typeof prorataToday === 'number' ? `€${prorataToday.toFixed(2)}` : '—'}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        ) : null}
                        {showProrataChargeCreditSplit || isProratedDueToday ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.75, fontSize: '0.65rem', lineHeight: 1.45 }}
                          >
                            {showProrataChargeCreditSplit ||
                            (planLineTotal?.prorationWindowStartIso != null &&
                              planLineTotal?.prorationWindowEndIso != null)
                              ? t('subscription.organization.planPage.subscriptionCheckoutProratedWindowFootnote', {
                                  fromDate: prorationWindowFromDisplay,
                                  toDate: prorationWindowToDisplay,
                                })
                              : t('subscription.organization.planPage.subscriptionCheckoutProratedChargeFootnote', {
                                  fromDate: chargePeriodStartLabel,
                                  toDate: prorataFootnotePeriodEndLabel,
                                })}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}

                {!cartLines.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {cart?.quotaAddons?.length
                      ? t('subscription.organization.planPage.subscriptionCheckoutAddonsWithoutPlan')
                      : t('subscription.organization.planPage.subscriptionCheckoutEmpty')}
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  /*
                   * Same flex basis/grow as the cart column so each row half is equal until this
                   * column hits maxWidth (never wider than the left column, max 350px).
                   * Percentage max-width on flex items is unreliable here vs. equal flex + cap.
                   */
                  flex: { xs: 'none', sm: '1 1 0%' },
                  maxWidth: { xs: '100%', sm: 350 },
                  minWidth: 0,
                  boxSizing: 'border-box',
                  minHeight: { xs: 'auto', sm: 0 },
                  overflowY: { xs: 'visible', sm: 'auto' },
                  borderLeft: { xs: 0, sm: 1 },
                  borderTop: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                  pl: { xs: 0, sm: 2 },
                  pt: { xs: 2, sm: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ mb: totals ? 3 : 0, width: '100%', flexShrink: 0 }}>
                  {isEditingBillingAddress ? (
                    <Box
                      component="form"
                      onSubmit={form.handleSubmit(billingAddressOnSubmit)}
                      display="flex"
                      flexDirection="column"
                      alignItems="end"
                    >
                      <BillingForm control={form.control} setValue={form.setValue} clearErrors={form.clearErrors} />
                      <Box display="flex" justifyContent="end" gap={1} mt={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsEditingBillingAddress(false);
                            billingAddressReset();
                          }}
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button type="submit" variant="contained" size="small" loading={editOrgPending}>
                          {t('common.save')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <BillingAddressDisplay
                      billingAddress={currentActiveOrganizationBillingAddress}
                      canEdit={canEditBillingAddress}
                      onEditClick={() => setIsEditingBillingAddress(true)}
                    />
                  )}
                </Box>

                {totals ? (
                  <Box
                    sx={{
                      width: '100%',
                      mt: { xs: 0, sm: 'auto' },
                      pb: 2,
                      flexShrink: 0,
                    }}
                  >
                    <OrganizationSubscriptionPricingDisplay
                      totals={totals}
                      latestOrderBillingCountry={latestOrderBillingCountry}
                      vatCountryDisplay={vatCountryDisplay}
                      loading={hasPendingCartMutation || cartLoading}
                      showBookedPlan={false}
                      showPlanLineBreakdown={false}
                    />
                    {recurringBillingSummaries.annual || recurringBillingSummaries.monthly ? (
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        {recurringBillingSummaries.annual ? (
                          <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
                              <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                                {t('subscription.organization.planPage.subscriptionCheckoutNewTotalAnnually')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                                €{recurringBillingSummaries.annual.total.toFixed(2)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {t('subscription.organization.planPage.subscriptionCheckoutNextChargeDate', {
                                excludingVat: t('event.checkoutModal.excludingVat'),
                                date: recurringBillingSummaries.annual.next,
                              })}
                            </Typography>
                          </Box>
                        ) : null}
                        {recurringBillingSummaries.monthly ? (
                          <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
                              <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                                {t('subscription.organization.planPage.subscriptionCheckoutNewTotalMonthly')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                                €{recurringBillingSummaries.monthly.total.toFixed(2)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {t('subscription.organization.planPage.subscriptionCheckoutNextChargeDate', {
                                excludingVat: t('event.checkoutModal.excludingVat'),
                                date: recurringBillingSummaries.monthly.next,
                              })}
                            </Typography>
                          </Box>
                        ) : null}
                      </Stack>
                    ) : null}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="p"
                      sx={{ mt: 2, lineHeight: 1.55, display: 'block' }}
                    >
                      {t('subscription.organization.planPage.subscriptionCheckoutLegalRenewalBeforeLink')}{' '}
                      <Link
                        href="https://app.britestage.com/plans"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                        variant="inherit"
                      >
                        {t('subscription.organization.planPage.subscriptionCheckoutLegalPlansLink')}
                      </Link>{' '}
                      {t('subscription.organization.planPage.subscriptionCheckoutLegalRenewalAfterLink')}
                    </Typography>
                    <CheckoutLegalProceedNotice />
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </Box>
        )}
      </Modal>

      <ManagePlanDialog
        key={
          planModalOpen && editingProductKey
            ? `checkout-manage-${organizationId}-${editingProductKey}-${JSON.stringify(cart?.lines ?? [])}`
            : 'closed'
        }
        open={planModalOpen}
        onClose={() => {
          setPlanModalOpen(false);
          setEditingProductKey(null);
        }}
        organizationId={organizationId}
        currentTier={manageDialogBookedTier}
        currentBillingPeriod={manageDialogBookedBillingPeriod}
        preferredBilling={
          (editingProductKey
            ? cartLines.find((l) => l.subscriptionProductKey === editingProductKey)?.billingPeriod
            : planChange?.billingPeriod) as SubscriptionBillingPeriod | undefined
        }
        canCompleteCheckout={canCompleteCheckout}
        embeddedInCheckout
        checkoutCartPlanChange={planChangeForManageDialog ?? null}
        checkoutCartLines={cartLines}
        editingSubscriptionProductKey={editingProductKey ?? undefined}
        subscriptionProductKeyFilter={editingProductKey ?? undefined}
        checkoutQuotaAddons={cart?.quotaAddons}
      />
    </>
  );
}
